import { Col, InputGroup, Row } from '@valid-eval/shared-react-components';
import moment from 'moment-timezone';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import CheckboxList from 'components/Form/CheckboxList';
import Date from 'components/Form/Date';
import Input from 'components/Form/Input';
import { blankNumberFormat, blankNumberNormalize } from 'components/FormFields/utils';
import { minValue, number } from 'components/FormFields/validations';
import { getSupportEmail } from 'config';
import { Event } from 'data/features/eventsTypes';
import { Phase } from 'data/features/phaseTypes';

import { validatePhaseEndDate, validatePhaseStartDate } from './utils';

type PhaseFormProps = {
  event: Event;
  phase?: Phase;
};

export function PhaseForm({ event, phase }: PhaseFormProps) {
  const { t } = useTranslation();
  const currentTimezone = moment.tz.guess();
  const timezone = moment.tz(currentTimezone).format('Z');
  const fieldPostfix = phase?.id ?? 'new';
  const methods = useFormContext();

  const email = getSupportEmail();
  const areDatesDisabled = !event?.start_date || !event?.end_date;
  const startDate = moment(event?.start_date).tz(currentTimezone);
  const endDate = moment(event?.end_date).tz(currentTimezone);

  const startDateValidation = (value: string) => {
    if (areDatesDisabled) return;
    return validatePhaseStartDate(value, methods.getValues()?.end_date, startDate, endDate);
  };

  const endDateValidation = (value: string) => {
    if (areDatesDisabled) return;
    return validatePhaseEndDate(value, methods.getValues()?.start_date, startDate, endDate);
  };

  return (
    <>
      <Row className="mb-2">
        <Col md={7}>
          <Input
            name="name"
            id={`name-${fieldPostfix}`}
            label={t('event.form.phase_name')}
            required
            autoFocus
          />
        </Col>
        <Col md={5}>
          <Input
            name="teams_anticipated_next_round"
            id={`teams_anticipated_next_round-${fieldPostfix}`}
            label={t('event.form.teams_anticipated_next_round')}
            required
            type="number"
            format={blankNumberFormat}
            normalize={blankNumberNormalize}
            rules={{ validate: { minValue: minValue(0), number } }}
          />
        </Col>
      </Row>
      <Row className="mb-2">
        <Col md={6}>
          <Date
            name="start_date"
            id={`start_date-${fieldPostfix}`}
            label={t('event.form.judging_window_opens')}
            isDateTime
            required
            after={
              <InputGroup.Text className="overflow-hidden">
                <small className="text-truncate">
                  {t('event.form.timezone_suffix', { timezone })}
                </small>
              </InputGroup.Text>
            }
            disabled={areDatesDisabled}
            rules={{ validate: { startDateValidation } }}
          />
        </Col>
        <Col md={6}>
          <Date
            name="end_date"
            id={`end_date-${fieldPostfix}`}
            label={t('event.form.judging_window_closes')}
            isDateTime
            required
            after={
              <InputGroup.Text className="overflow-hidden">
                <small className="text-truncate">
                  {t('event.form.timezone_suffix', { timezone })}
                </small>
              </InputGroup.Text>
            }
            disabled={areDatesDisabled}
            rules={{ validate: { endDateValidation } }}
          />
        </Col>
      </Row>
      {areDatesDisabled && (
        <Row>
          <Col>
            <div className="alert alert-warning d-flex align-items-center justify-content-center">
              <i className="fas fa-exclamation-circle me-2 text-orange01 me-3" />
              <span>{t('settings.error.judging_window_disabled', { email })}</span>
            </div>
          </Col>
        </Row>
      )}
      <Row className="mb-2">
        <Col md={6}>
          <Date
            name="consensus_meeting_date"
            id={`consensus_meeting_date-${fieldPostfix}`}
            label={t('event.form.consensus_meeting_date')}
            required
          />
        </Col>
        <Col md={6}>
          <Date
            name="feedback_available_date"
            id={`feedback_available_date-${fieldPostfix}`}
            label={t('event.form.feedback_available_date')}
            required
          />
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <CheckboxList
            id={`artifact_ids_checkboxes-${fieldPostfix}`}
            name="artifact_ids_checkboxes"
            label={t('event.form.phase_artifacts')}
            options={event.artifacts.map((artifact) => ({
              value: String(artifact.id),
              label: artifact.teams_and_organizers_name,
            }))}
            toggleButton
          />
        </Col>
      </Row>
      <button className="d-none" type="submit" />
    </>
  );
}
